import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { PageHeader } from "@/components/PageHeader";
import { ProductTable } from "./components/ProductTable";
import { ProductPricingDialog } from "./components/ProductPricingDialog";
import { useProducts } from "./hooks/useProducts";
import { useRawItems } from "@/pages/insumos/hooks/useRawItems";
import { pricingModeFromGraphql } from "@/lib/pricing";
import type { Product } from "@/types/models";

export default function ProductsPage() {
  const {
    store,
    products,
    loading,
    error,
    updateProduct,
    archiveProduct,
    deleteProduct,
    archiving,
    deleting,
    saving,
  } = useProducts();
  const { rawItems, loading: rawItemsLoading } = useRawItems();

  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pricingProduct, setPricingProduct] = useState<Product | null>(null);
  const [pricingOpen, setPricingOpen] = useState(false);

  const handleArchive = (product: Product) => {
    setMessage(null);
    setActionError(null);
    archiveProduct(product.id, {
      onCompleted: () => setMessage(`"${product.name}" arquivado.`),
      onError: (msg) => setActionError(msg),
    });
  };

  const handleDelete = (product: Product) => {
    if (!window.confirm(`Excluir o produto "${product.name}"?`)) return;

    setMessage(null);
    setActionError(null);
    deleteProduct(product.id, {
      onCompleted: () => setMessage("Produto excluído."),
      onError: (msg) => setActionError(msg),
    });
  };

  const handleOpenPricing = (product: Product) => {
    setPricingProduct(product);
    setPricingOpen(true);
  };

  if (loading || rawItemsLoading) {
    return (
      <div className="container-elegant page-padding space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="container-elegant page-padding">
        <PageHeader title="Produtos" />
        <section className="max-w-xl border border-primary-200 dark:border-primary-800 panel-padding">
          <h2 className="text-xl font-light text-primary-900 dark:text-white mb-3">
            Crie sua loja primeiro
          </h2>
          <p className="text-sm font-light text-primary-600 dark:text-primary-400 mb-6">
            Você precisa configurar uma loja antes de cadastrar produtos.
          </p>
          <Button asChild>
            <Link to="/store/settings">CONFIGURAR LOJA</Link>
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="container-elegant page-padding">
      <PageHeader
        title="Produtos"
        description={`Gerencie o catálogo da loja ${store.name}.`}
        action={
          <Button asChild>
            <Link to="/products/new">Novo produto</Link>
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>
            {error.message || "Não foi possível carregar os produtos."}
          </AlertDescription>
        </Alert>
      )}

      {actionError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {message && (
        <p className="mb-6 text-sm font-light text-green-700 dark:text-green-400">{message}</p>
      )}

      <ProductTable
        products={products}
        archiving={archiving}
        deleting={deleting}
        onArchive={handleArchive}
        onDelete={handleDelete}
        onPrice={handleOpenPricing}
      />

      <ProductPricingDialog
        product={pricingProduct}
        open={pricingOpen}
        onOpenChange={setPricingOpen}
        rawItems={rawItems}
        storeMarkupPercent={store.defaultMarkupPercent}
        storeTargetNetMarginPercent={store.defaultTargetNetMarginPercent}
        storePricingMode={pricingModeFromGraphql(store.defaultPricingMode)}
        storeTaxPercent={store.effectiveTaxPercent}
        saving={saving}
        onSave={updateProduct}
      />
    </div>
  );
}
